import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SidebarProvider, useSidebar } from '../Sidebar/SidebarContext';
import Sidebar from '../Sidebar';
import ConfirmationModal from '../Modals/ConfirmationModal';
import { UnsavedChangesProvider } from '../../contexts/UnsavedChangesContext';
import ContentHeader from '../ContentHeader';
import GlobalSearchModal from '../ContentHeader/GlobalSearchModal';
import { ImpersonationBanner } from '../ImpersonationBanner';
import { useImpersonationListener } from '../../hooks/useImpersonationListener';

/** Owns the global search modal, whose trigger lives in the sidebar. */
const GlobalSearch = () => {
  const { isSearchOpen, openSearch, closeSearch } = useSidebar();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openSearch();
      }
    },
    [openSearch]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return <GlobalSearchModal isOpen={isSearchOpen} onClose={closeSearch} />;
};

function UnsavedChangesPrompt({
  isOpen,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={onCancel}
      onConfirm={onConfirm}
      title={t('unsavedChanges')}
      message={t('unsavedChangesMessage')}
      confirmText={t('discardChanges')}
      cancelText={t('keepEditing')}
      variant="danger"
    />
  );
}

const Layout = () => {
  const impersonating = useImpersonationListener();
  const [, forceUpdate] = useState(0);
  const { pathname } = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Send every route to its own top.
  //
  // The page does not scroll — this div does — so the browser's own
  // scroll-to-top on navigation never applied, and React Router does not touch
  // a scroller it did not create. The container simply kept whatever scrollTop
  // it had: open a record from halfway down a list and the detail view opened
  // halfway down too, which read as the page auto-scrolling on arrival.
  //
  // Layout effect, not a plain one: it runs before paint, so the new route is
  // never painted at the old offset and then yanked upward.
  useLayoutEffect(() => {
    // Assigning scrollTop rather than calling scrollTo: it is unconditionally
    // instant, and jsdom implements the property but not the method.
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [pathname]);

  return (
    <UnsavedChangesProvider>
      {({ isOpen, onConfirm, onCancel }) => (
        <>
          <SidebarProvider>
            {impersonating && <ImpersonationBanner onEnded={() => forceUpdate(n => n + 1)} />}
            <div className="flex h-dvh bg-ds-bg md-768:h-screen">
              <Sidebar />
              <main
                // The content area is the gray page surface white cards float on. It
                // was white, which is why every card read as flush with the page.
                //
                // Square, like SP's shell. A `rounded-l-xl` here curved the top-left
                // corner against the sidebar, leaving a notch beside the page header.
                className="flex-1 flex flex-col bg-ds-bg-secondary min-w-0 overflow-hidden"
              >
                <ContentHeader />
                <div ref={scrollRef} className="flex-1 overflow-y-auto">
                  <Outlet />
                </div>
              </main>
            </div>
            <GlobalSearch />
          </SidebarProvider>
          <UnsavedChangesPrompt isOpen={isOpen} onConfirm={onConfirm} onCancel={onCancel} />
        </>
      )}
    </UnsavedChangesProvider>
  );
};

export default Layout;
